import { Color, DirectionalLight, Fog, HemisphereLight, Scene, type Vector3, type WebGLRenderer } from 'three';
import type { Sky } from 'three/examples/jsm/objects/Sky.js';
import { SKY, createSky, sunDirection } from './sky';

export interface SceneRig {
  scene: Scene;
  sun: DirectionalLight;
  hemi: HemisphereLight;
  sky: Sky;
  /** Where the sun (or the moon at night) is; the atmosphere changes it. */
  sunDirection: Vector3;
}

const SUN_DISTANCE = 70;

/** Hazy tropical afternoon: a physical sky lights the scene; the sun casts the shadows. */
export function createScene(renderer: WebGLRenderer): SceneRig {
  const scene = new Scene();
  const { sky, environment } = createSky(renderer);
  scene.environment = environment;
  // Sky light fills shadows and gives reflections; kept low so the sun still leads.
  scene.environmentIntensity = 0.28;
  scene.background = new Color(SKY.haze);
  scene.fog = new Fog(SKY.haze, 120, 380);
  const hemi = new HemisphereLight('#e8e4da', '#5e4b36', 0.35);
  scene.add(hemi);

  const sun = new DirectionalLight('#ffe9c9', 2.5);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.bias = -0.0004;
  sun.shadow.normalBias = 0.02;
  const cam = sun.shadow.camera;
  cam.left = -45;
  cam.right = 45;
  cam.top = 70;
  cam.bottom = -50;
  cam.far = 220;
  scene.add(sun, sun.target, sky);
  return { scene, sun, hemi, sky, sunDirection: sunDirection() };
}

/** Shadow quality for the current tier; 0 turns the shadow pass off entirely. */
export function setShadowQuality(rig: SceneRig, mapSize: number): void {
  rig.sun.castShadow = mapSize > 0;
  if (mapSize > 0 && rig.sun.shadow.mapSize.x !== mapSize) {
    rig.sun.shadow.mapSize.set(mapSize, mapSize);
    rig.sun.shadow.map?.dispose();
    rig.sun.shadow.map = null;
  }
}

/** Keep the sun's shadow box and the sky over the bus as it drives. */
export function followSun(rig: SceneRig, busZ: number): void {
  const dir = rig.sunDirection;
  rig.sun.position.set(dir.x * SUN_DISTANCE, dir.y * SUN_DISTANCE, busZ - 30 + dir.z * SUN_DISTANCE);
  rig.sun.target.position.set(0, 0, busZ - 30);
  rig.sky.position.set(0, 0, busZ);
}
